import VerticalDotsIcon from "@assets/icons/new/vertical-dots_28px.svg?react";
import { SphereIconButton } from "@components/common/sphere-icon-button";
import { UserDisplay } from "@components/common/user-display";
import { RemoveMemberSuccessDescription } from "@components/remove-member-success-description";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import { SdbMemberTypes } from "@custom-types/member-types";
import { BaseTeamProps } from "@custom-types/teams-types";
import { Menu, MenuItem } from "@flat-ui";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useMembersUtils } from "@hooks/use-member-utils";
import { useToast } from "@hooks/use-toast";
import { Typography } from "@mui/material";
import { TEAM_DISPLAY_NAME } from "@src/constants/team-constants";
import { fetchingMembersFlagsSelector } from "@store/members/members-selector";
import { removeMemberFromCompany } from "@store/members/members-slice-thunk";
import { selectedSdbCompanyNameSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { fetchingTeamsFlagsSelector } from "@store/teams/teams-selector";
import { removeMembersByIds } from "@store/teams/teams-slice";
import { removeMembersFromTeam } from "@store/teams/teams-slice-thunk";
import {
  TableType,
  TeamMemberEvents,
  WorkspaceMemberEvents,
} from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useMemo, useRef, useState } from "react";
import { Dialog } from "@flat-ui";
import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/faro-dialog";

interface Props extends BaseCompanyIdProps, BaseTeamProps {
  /** The member of a team */
  member: SdbMemberTypes;
}

/** Determine from where a member should be removed */
enum RemoveDialogEntity {
  /** Determine if the member should be removed from team */
  team = `${TEAM_DISPLAY_NAME}`,

  /** Determine if the member should be removed from workspace */
  workspace = "workspace",
}

/** Renders actions dropdown for team members */
export function TeamMemberActions({
  member,
  companyId,
  team,
}: Props): JSX.Element {
  const { openProfilePage } = useMembersUtils();
  const { trackEvent } = useTrackEvent();
  const dispatch = useAppDispatch();
  const { isRemovingTeamMember } = useAppSelector(fetchingTeamsFlagsSelector);
  const { isRemovingCompanyMember } = useAppSelector(
    fetchingMembersFlagsSelector
  );
  const companyName = useAppSelector(selectedSdbCompanyNameSelector);
  const { canDeleteMemberFromCompany, canDeleteMembersFromTeam } =
    useHasUserValidRoleCompanyLevel();
  const { showToast } = useToast();

  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [removeDialogEntity, setRemoveDialogEntity] =
    useState<RemoveDialogEntity | null>(null);

  /** Determining if the confirm button should be in a loading state */
  const isRemoving = useMemo(
    () =>
      removeDialogEntity === RemoveDialogEntity.team
        ? isRemovingTeamMember
        : isRemovingCompanyMember,
    [isRemovingCompanyMember, isRemovingTeamMember, removeDialogEntity]
  );

  function onOpenProfile(): void {
    openProfilePage({
      userIdentity: member.identity,
      source: "team members table",
    });
  }

  /** Determines remove member action */
  // eslint-disable-next-line @typescript-eslint/require-await -- Please review lint error
  async function removeMember(): Promise<void> {
    switch (removeDialogEntity) {
      case RemoveDialogEntity.team:
        void removeMemberFromTeam();
        break;

      case RemoveDialogEntity.workspace:
        void removeCompanyMember();
        break;
    }
  }

  /** Handles removing a team member */
  async function removeMemberFromTeam(): Promise<void> {
    trackEvent({
      name: TeamMemberEvents.removeMemberFromTeam,
      props: { dataType: TableType.teamMembers },
    });

    try {
      await dispatch(
        removeMembersFromTeam({
          companyId,
          teamId: team.id,
          memberIds: [member.identity],
        })
      ).unwrap();

      showToast({
        message: `Member removed from ${TEAM_DISPLAY_NAME}`,
        description: (
          <RemoveMemberSuccessDescription
            member={member}
            subjectType="team"
            entityName={team.name}
          />
        ),
        type: "success",
      });
    } finally {
      setRemoveDialogEntity(null);
    }
  }

  /** Handles removing a company member */
  async function removeCompanyMember(): Promise<void> {
    trackEvent({
      name: WorkspaceMemberEvents.removeMember,
      props: { dataType: TableType.teamMembers },
    });

    try {
      await dispatch(removeMemberFromCompany({ companyId, member })).unwrap();

      dispatch(removeMembersByIds([member.identity]));

      showToast({
        message: "Member removed from workspace",
        description: (
          <RemoveMemberSuccessDescription
            member={member}
            subjectType="workspace"
            entityName={companyName ?? ""}
          />
        ),
        type: "success",
      });
    } finally {
      setRemoveDialogEntity(null);
    }
  }

  return (
    <>
      <SphereIconButton
        aria-label="more"
        aria-haspopup="true"
        component={VerticalDotsIcon}
        onClick={() => setIsMenuOpen(true)}
        iconButtonProps={{ ref: anchorRef }}
      />
      <Menu
        open={isMenuOpen}
        anchorEl={anchorRef.current}
        onClose={() => setIsMenuOpen(false)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem label="Profile" onClick={onOpenProfile} />
        {canDeleteMembersFromTeam && !team.category.isManaged && (
          <MenuItem
            label={`Remove from ${TEAM_DISPLAY_NAME}`}
            onClick={() => setRemoveDialogEntity(RemoveDialogEntity.team)}
          />
        )}
        {canDeleteMemberFromCompany && (
          <MenuItem
            label="Remove from workspace"
            onClick={() => setRemoveDialogEntity(RemoveDialogEntity.workspace)}
          />
        )}
      </Menu>

      {/* Dialog with the details of the member to be removed */}
      <Dialog
        title={`Remove from ${removeDialogEntity}`}
        confirmText="Remove"
        open={removeDialogEntity !== null}
        onConfirm={removeMember}
        onCancel={() => setRemoveDialogEntity(null)}
        showSpinner={isRemoving}
      >
        <Typography
          sx={{
            fontSize: "14px",
            marginTop: "0px",
            marginBottom: SPACE_ELEMENTS_OF_MODAL,
          }}
        >
          When removing member from {removeDialogEntity} they will no longer be
          able to access this {removeDialogEntity}. You can always invite this
          member again.
        </Typography>

        <UserDisplay member={member} />
      </Dialog>
    </>
  );
}
